import { useState, type FormEvent } from 'react';
import { chooseAthlete } from '../api/coach-booking.js';

/**
 * Add a new athlete, on the coach's Book (stage C; canvas row 11, approved 16
 * October): the athlete's name, and the parent's name, phone and email. Next
 * goes to the calendar for them, as choosing somebody found does; the family
 * is started only when an hour is booked.
 *
 * Phone and email are optional (ruled 23 October: the coach can send the
 * sign-up link some other way). Next stays grey until the four names are
 * filled, and a phone or email typed looks like one.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** As the server reads a phone (phone-number.ts): ten digits, 1 and ten, or + and the country. */
function phoneLooksRight(text: string): boolean {
  const digits = text.replace(/[^0-9]/g, '');
  if (text.startsWith('+')) return digits.length >= 7 && digits.length <= 15 && !digits.startsWith('0');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
}

export function NewAthleteForm({ firstGuess, onBack }: { firstGuess: string; onBack: () => void }) {
  const [guessFirst, ...guessRest] = firstGuess.trim().split(/\s+/);
  const [athleteFirst, setAthleteFirst] = useState(capitalised(guessFirst ?? ''));
  const [athleteLast, setAthleteLast] = useState(capitalised(guessRest.join(' ')));
  const [parentFirst, setParentFirst] = useState('');
  const [parentLast, setParentLast] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [parentEmail, setParentEmail] = useState('');

  const ready =
    [athleteFirst, athleteLast, parentFirst, parentLast].every((value) => value.trim()) &&
    (parentPhone.trim() === '' || phoneLooksRight(parentPhone.trim())) &&
    (parentEmail.trim() === '' || EMAIL.test(parentEmail.trim()));

  function next(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;
    chooseAthlete({
      new: true,
      name: `${athleteFirst.trim()} ${athleteLast.trim()}`,
      athlete_first_name: athleteFirst.trim(),
      athlete_last_name: athleteLast.trim(),
      parent_first_name: parentFirst.trim(),
      parent_last_name: parentLast.trim(),
      parent_phone: parentPhone.trim(),
      parent_email: parentEmail.trim().toLowerCase(),
    });
  }

  return (
    <form className="new-athlete" onSubmit={next} noValidate>
      <div className="page-head">
        <button type="button" className="back" aria-label="Back to Book" onClick={onBack}>
          ‹
        </button>
        <h2>Add a new athlete</h2>
      </div>
      <div className="panel">
        <span className="section-label">Athlete</span>
        <Field
          id="athlete-first"
          label="First name"
          value={athleteFirst}
          onChange={setAthleteFirst}
          autoComplete="off"
        />
        <Field
          id="athlete-last"
          label="Last name"
          value={athleteLast}
          onChange={setAthleteLast}
          autoComplete="off"
        />
        <span className="section-label">Adult</span>
        <Field
          id="parent-first"
          label="First name"
          value={parentFirst}
          onChange={setParentFirst}
          autoComplete="off"
        />
        <Field
          id="parent-last"
          label="Last name"
          value={parentLast}
          onChange={setParentLast}
          autoComplete="off"
        />
        <Field
          id="parent-phone"
          label="Phone"
          type="tel"
          value={parentPhone}
          onChange={setParentPhone}
          autoComplete="off"
        />
        <Field
          id="parent-email"
          label="Email"
          type="email"
          value={parentEmail}
          onChange={setParentEmail}
          autoComplete="off"
        />
      </div>
      <button type="submit" className="primary" disabled={!ready}>
        Next
      </button>
    </form>
  );
}

function capitalised(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  type?: string;
  autoComplete?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        autoComplete={autoComplete}
        autoCapitalize={type === 'email' ? 'none' : 'words'}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
